import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { Unlock, Search, AlertCircle, CheckCircle, ShieldAlert } from 'lucide-react';

export function UnlockAccountView() {
  const { profile } = useAuth();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [lockedAccounts, setLockedAccounts] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Only allow Superadmins (role casing varies across seed data, so compare case-insensitively)
  if ((profile?.role || '').toLowerCase() !== 'superadmin') {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="bg-red-50 rounded-lg shadow-sm border border-red-200 p-8">
          <div className="flex items-center gap-3 mb-4">
            <ShieldAlert className="w-8 h-8 text-red-600" />
            <h2 className="text-2xl font-bold text-red-900">Access Denied</h2>
          </div>
          <p className="text-red-800">
            You do not have permission to access this page. Only Superadmins can unlock user accounts.
          </p>
        </div>
      </div>
    );
  }

  async function handleUnlock(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const { data, error } = await supabase.rpc('unlock_login_account', {
        user_email: email.toLowerCase()
      });

      if (error) throw error;

      if (data.success) {
        setMessage({ type: 'success', text: `Account ${email} has been unlocked successfully.` });
        setEmail('');
        loadLockedAccounts();
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to unlock account' });
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || 'An error occurred' });
    } finally {
      setLoading(false);
    }
  }

  async function loadLockedAccounts() {
    setSearchLoading(true);
    try {
      const { data, error } = await supabase
        .from('login_attempts')
        .select('*')
        .eq('is_locked', true)
        .order('locked_until', { ascending: false });

      if (error) throw error;
      setLockedAccounts(data || []);
    } catch (error) {
      console.error('Error loading locked accounts:', error);
    } finally {
      setSearchLoading(false);
    }
  }

  async function quickUnlock(accountEmail: string) {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc('unlock_login_account', {
        user_email: accountEmail
      });

      if (error) throw error;

      if (data.success) {
        setMessage({ type: 'success', text: `Account ${accountEmail} unlocked.` });
        loadLockedAccounts();
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-6">
          <Unlock className="w-6 h-6 text-blue-600" />
          <h2 className="text-2xl font-bold text-slate-900">Unlock User Account</h2>
        </div>

        {message && (
          <div
            className={`mb-4 p-4 rounded-lg flex items-start gap-3 ${
              message.type === 'success'
                ? 'bg-green-50 text-green-800 border border-green-200'
                : 'bg-red-50 text-red-800 border border-red-200'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            )}
            <span>{message.text}</span>
          </div>
        )}

        <form onSubmit={handleUnlock} className="space-y-4 mb-8">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              User Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              required
            />
            <p className="mt-1 text-sm text-slate-500">
              Enter the email address of the account you want to unlock
            </p>
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={loading || !email}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Unlock className="w-4 h-4" />
              {loading ? 'Unlocking...' : 'Unlock Account'}
            </button>

            <button
              type="button"
              onClick={loadLockedAccounts}
              disabled={searchLoading}
              className="px-6 py-2 bg-slate-600 text-white rounded-lg font-medium hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Search className="w-4 h-4" />
              {searchLoading ? 'Loading...' : 'View Locked Accounts'}
            </button>
          </div>
        </form>

        {lockedAccounts.length > 0 && (
          <div className="border-t border-slate-200 pt-6">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">
              Currently Locked Accounts ({lockedAccounts.length})
            </h3>
            <div className="space-y-3">
              {lockedAccounts.map((account) => (
                <div
                  key={account.id}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200"
                >
                  <div className="flex-1">
                    <p className="font-medium text-slate-900">{account.email}</p>
                    <div className="flex gap-4 mt-1 text-sm text-slate-600">
                      <span>Attempts: {account.attempt_count}</span>
                      <span>
                        Locked until:{' '}
                        {new Date(account.locked_until).toLocaleString()}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => quickUnlock(account.email)}
                    disabled={loading}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <Unlock className="w-4 h-4" />
                    Unlock
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {lockedAccounts.length === 0 && searchLoading === false && (
          <div className="border-t border-slate-200 pt-6">
            <p className="text-center text-slate-500">
              No locked accounts found. Click "View Locked Accounts" to check.
            </p>
          </div>
        )}

        <div className="mt-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-semibold text-blue-900 mb-2">How Rate Limiting Works</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Users get 5 login attempts within 15 minutes</li>
            <li>• After 5 failed attempts, account is locked for 30 minutes</li>
            <li>• Successful login clears all failed attempts</li>
            <li>• Only Superadmins can manually unlock accounts</li>
            <li>• Old records are automatically cleaned up after 7 days</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
